// Material ui
/* eslint-disable import/extensions,no-console */
import { ChakraBaseProvider } from '@chakra-ui/react';
import CssBaseline from '@material-ui/core/CssBaseline';
import theme from 'common/theme';
import ErrorBoundary from 'components/ErrorFallbackComponents/ErrorBoundary';
import { LiveEventPreviewProvider } from 'components/LiveEventPreview/useLiveEventPreview';
import EmotionCacheProvider from 'context/EmotionCacheProvider';
import { FirebaseAuthProvider } from 'hooks/useFirebaseAuth';
import usePrerender from 'hooks/usePrerender';
import { UserSubscriberProvider } from 'hooks/useUserSubscriber';
import React, { Suspense, useEffect, useState } from 'react';
import MaterialUiProvider from './Assets/Themes/MaterialUiProvider';
import Routes from './Routes';
import Fonts from 'common/typography/Fonts';
import type { SetTimeoutReturnType } from 'types';

const InitFirebasePerformance = React.lazy(
  () => import('lib/firebase/InitFirebasePerformance')
);

const LazyToastContainer = React.lazy(() =>
  import('react-toastify').then(module => ({
    default: module.ToastContainer
  }))
);

const InitNumeral = React.lazy(() => import('components/InitNumeral'));

const Silktide = React.lazy(() => import('lib/silktide'));

let LazyLoadCSS: React.ComponentType;
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  LazyLoadCSS = require('lib/css/LazyLoadCSS').default;
} else {
  LazyLoadCSS = React.lazy(() => import('lib/css/LazyLoadCSS'));
}

const DeferredIdleInitializers = () => {
  const [idleReady, setIdleReady] = useState(false);

  useEffect(() => {
    let didCancel = false;
    const onIdle = () => {
      if (!didCancel) {
        setIdleReady(true);
      }
    };

    const idleHandle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(onIdle, { timeout: 4000 })
        : window.setTimeout(onIdle, 1000);

    return () => {
      didCancel = true;
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle as number);
      } else {
        clearTimeout(idleHandle as number);
      }
    };
  }, []);

  if (!idleReady) {
    return null;
  }

  return (
    <>
      <Suspense fallback={<></>}>
        <Silktide />
      </Suspense>
      <Suspense fallback={<></>}>
        <LazyToastContainer />
      </Suspense>
      <Suspense fallback={<></>}>
        <InitFirebasePerformance />
      </Suspense>
      <Suspense fallback={<></>}>
        <InitNumeral />
      </Suspense>
    </>
  );
};

const App: React.FC = () => {
  const { isPrerender } = usePrerender();

  useEffect(() => {
    let timeoutJob: SetTimeoutReturnType;
    if (isPrerender) {
      timeoutJob = setTimeout(() => {
        window.prerenderReady = true;
      }, 40000); // max prerender timeout 40s
    }
    return () => {
      clearTimeout(timeoutJob);
    };
  }, [isPrerender]);

  return (
    <div className="bg-white">
      <EmotionCacheProvider>
        <Fonts />
        <ChakraBaseProvider theme={theme}>
          <ErrorBoundary>
            <MaterialUiProvider>
              <CssBaseline />
              <FirebaseAuthProvider>
                <UserSubscriberProvider>
                  <LiveEventPreviewProvider>
                    <Routes />
                  </LiveEventPreviewProvider>
                </UserSubscriberProvider>
              </FirebaseAuthProvider>
            </MaterialUiProvider>
          </ErrorBoundary>
        </ChakraBaseProvider>
      </EmotionCacheProvider>
      <DeferredIdleInitializers />
      {!isPrerender && (
        <Suspense fallback={<></>}>
          <LazyLoadCSS />
        </Suspense>
      )}
    </div>
  );
};

export default App;
